<template>
  <div class="campaign-report-page">
    <section>
      <div class="py-8 px-8">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-3">
          <div>
            <div class="section-kicker">CAMPAIGN REPORT</div>
            <h4 class="mb-1">{{ campaign ? campaign.name : 'Campaign' }}</h4>
            <p class="text-muted mb-0">Monitor delivery progress and review every campaign recipient.</p>
          </div>
          <router-link to="/marketing/campaigns" class="btn btn-light">
            <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-1" />Campaigns
          </router-link>
        </div>
      </div>
    </section>

    <section class="px-8 mb-6">
      <div v-if="loading" class="page-loading">
        <div class="spinner-border text-primary"></div>
        <span>Loading campaign report…</span>
      </div>

      <div v-else-if="loadError" class="content-card empty-report text-center">
        <div class="empty-icon"><AppIcon name="bi-exclamation-circle" class="bi bi-exclamation-circle" /></div>
        <h5>Campaign report unavailable</h5>
        <p class="text-muted mb-3">{{ loadError }}</p>
        <button class="btn btn-outline-primary" @click="loadCampaign">Try again</button>
      </div>

      <div v-else-if="campaign" class="content-card report-content">
        <div class="d-flex flex-wrap justify-content-end gap-2 mb-3">
          <button class="btn btn-light" :disabled="refreshing || retryingFailed" @click="refreshCampaign">
            <span v-if="refreshing" class="spinner-border spinner-border-sm me-2"></span>
            <AppIcon name="bi-arrow-clockwise" v-else class="bi bi-arrow-clockwise me-1" />Refresh report
          </button>
          <button
            v-if="canManage && campaign.stats.failed"
            class="btn btn-outline-danger"
            :disabled="retryingFailed"
            @click="retryCampaignFailures(campaign)"
          >
            <span v-if="retryingFailed" class="spinner-border spinner-border-sm me-2"></span>
            <AppIcon name="bi-arrow-repeat" v-else class="bi bi-arrow-repeat me-1" />Retry all {{ campaign.stats.failed }} errors
          </button>
        </div>

        <div class="row g-3 mb-4">
          <div v-for="item in reportMetrics" :key="item.label" class="col-6 col-md-3">
            <div class="report-metric">
              <span>{{ item.label }}</span>
              <strong :class="item.className">{{ item.value }}</strong>
            </div>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table align-middle">
            <thead>
              <tr>
                <th>Lead</th>
                <th>Channel</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Scheduled</th>
                <th>Result</th>
                <th v-if="canManage">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="recipient in campaign.recipients" :key="recipient.id">
                <td>
                  <strong>{{ recipient.lead ? recipient.lead.name : 'Deleted lead' }}</strong>
                  <small class="d-block text-muted">{{ recipientContact(recipient) }}</small>
                </td>
                <td>{{ recipient.channel }}</td>
                <td><span class="status-pill" :class="recipient.status">{{ recipient.status }}</span></td>
                <td>{{ recipient.attempts || 0 }}</td>
                <td>{{ formatDate(recipient.scheduled_at) }}</td>
                <td class="delivery-result">{{ recipient.error || recipient.provider_message_id || '—' }}</td>
                <td v-if="canManage">
                  <button
                    v-if="recipient.status === 'failed'"
                    class="btn btn-sm btn-outline-warning"
                    title="Retry this failed message"
                    :disabled="retryingFailed"
                    @click="retryCampaignFailures(campaign, [recipient.id])"
                  >
                    <AppIcon name="bi-arrow-repeat" class="bi bi-arrow-repeat me-1" />Retry
                  </button>
                </td>
              </tr>
              <tr v-if="!campaign.recipients.length">
                <td :colspan="canManage ? 7 : 6" class="text-center text-muted py-5">No delivery events yet.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import helper from '../../helper'

export default {
  name: 'MarketingCampaignReport',
  data() {
    return {
      campaign: null,
      loading: true,
      refreshing: false,
      retryingFailed: false,
      loadError: ''
    }
  },
  computed: {
    canManage() {
      return this.$store.getters.ROLE === 'owner' || this.$store.getters.PERMISSIONS.includes('manage_marketing')
    },
    reportMetrics() {
      const stats = this.campaign && this.campaign.stats || {}
      return [
        { label: 'Total', value: stats.total || 0 },
        { label: 'Sent', value: stats.sent || 0, className: 'text-success' },
        { label: 'Failed', value: stats.failed || 0, className: 'text-danger' },
        { label: 'Skipped', value: stats.skipped || 0 }
      ]
    }
  },
  watch: {
    '$route.params.id': 'loadCampaign'
  },
  mounted() {
    this.loadCampaign()
  },
  methods: {
    async loadCampaign() {
      this.loading = true
      this.loadError = ''
      try {
        await this.fetchCampaign()
      } catch (error) {
        this.loadError = error.message || 'The campaign could not be loaded.'
        this.$toast.error(this.loadError)
      } finally {
        this.loading = false
      }
    },
    async refreshCampaign() {
      this.refreshing = true
      try {
        await this.fetchCampaign()
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.refreshing = false
      }
    },
    async fetchCampaign() {
      const response = await helper.customGet(`${process.env.VUE_APP_API}/marketing/campaigns/${this.$route.params.id}`)
      const details = response.data || {}
      this.campaign = {
        ...details,
        stats: details.stats && typeof details.stats === 'object' ? details.stats : {},
        recipients: Array.isArray(details.recipients) ? details.recipients : []
      }
    },
    async retryCampaignFailures(campaign, recipientIds = []) {
      const count = recipientIds.length || campaign.stats && campaign.stats.failed || 0
      if (!count) return

      const result = await this.$swal({
        title: `Retry ${count} failed message${count === 1 ? '' : 's'}?`,
        text: 'Fix the provider connection first. Only failed messages will be requeued; sent messages are never retried.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Retry failed'
      })
      if (!result.isConfirmed) return

      try {
        this.retryingFailed = true
        const response = await helper.customPost(
          `${process.env.VUE_APP_API}/marketing/campaigns/${campaign.id}/retry-failed`,
          recipientIds.length ? { recipient_ids: recipientIds } : {},
          { showToast: false }
        )
        if (!response.data.retried) {
          this.$toast.error(response.data.message || 'No failed messages were queued')
          return
        }
        this.$toast.open({ message: response.data.message, type: 'success', position: 'bottom' })
        await this.fetchCampaign()
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.retryingFailed = false
      }
    },
    recipientContact(recipient) {
      if (!recipient.lead) return ''
      return recipient.channel === 'email' ? recipient.lead.email : recipient.lead.phone
    },
    formatDate(value) {
      if (!value) return '—'
      return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
    }
  }
}
</script>

<style scoped>
.campaign-report-page {
  min-height: 100vh;
}

.section-kicker {
  display: block;
  color: #6574d8;
  font-size: .66rem;
  font-weight: 800;
  letter-spacing: .13em;
  margin-bottom: .35rem;
}

.page-loading {
  min-height: 360px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 1rem;
  color: #7c8493;
}

.content-card {
  overflow: hidden;
  background: #fff;
  border: 1px solid #e4e7ec;
  border-radius: .65rem;
  box-shadow: 0 2px 7px rgba(25, 35, 50, .035);
}

.report-content {
  padding: 1rem;
}

.empty-report {
  padding: 4.5rem 1rem;
}

.empty-icon {
  width: 58px;
  height: 58px;
  border-radius: .85rem;
  background: #feeaea;
  color: #b43b3b;
  display: grid;
  place-items: center;
  margin: 0 auto 1rem;
  font-size: 1.35rem;
}

.report-metric {
  background: #f7f8fa;
  border-radius: .55rem;
  padding: 1rem;
}

.report-metric span,
.report-metric strong {
  display: block;
}

.report-metric span {
  font-size: .67rem;
  color: #7d8696;
}

.report-metric strong {
  font-size: 1.3rem;
  margin-top: .2rem;
}

.status-pill {
  display: inline-flex;
  padding: .28rem .58rem;
  border-radius: 999px;
  background: #eef0f4;
  color: #5f6877;
  font-size: .68rem;
  font-weight: 700;
  text-transform: capitalize;
}

.status-pill.sent,
.status-pill.completed {
  background: #e6f8ed;
  color: #187548;
}

.status-pill.failed {
  background: #feeaea;
  color: #b43b3b;
}

.status-pill.scheduled,
.status-pill.queued,
.status-pill.sending,
.status-pill.running {
  background: #e8efff;
  color: #315dbb;
}

.status-pill.paused {
  background: #fff2db;
  color: #996614;
}

.status-pill.skipped {
  background: #f1ecfb;
  color: #7149aa;
}

.delivery-result {
  max-width: 230px;
  white-space: normal;
  color: #747d8c;
  font-size: .7rem;
}

.btn {
  border-radius: .5rem;
}

@media (max-width: 767px) {
  .campaign-report-page .px-8 {
    padding-right: 2rem !important;
    padding-left: 2rem !important;
  }

  .campaign-report-page .py-8 {
    padding-top: 2rem !important;
    padding-bottom: 2rem !important;
  }

  .table {
    min-width: 900px;
  }
}
</style>
